/**
 * keyboardScrollNav — Sep 2026 (user request)
 *
 * Global arrow-key scrolling for scrollable menus, tables, lists and
 * dialogs.  Browsers only scroll a container with the keyboard when
 * that exact element is focused — our scrollable divs/tables (e.g. the
 * Claims Workshop's wide table) aren't focusable, so arrow keys did
 * nothing.  Instead of adding tabIndex to hundreds of containers, one
 * document-level keydown handler finds the right scroll container and
 * scrolls it:
 *
 *   1. Never interferes with typing (inputs/textareas/selects/
 *      contenteditable) or with widgets that own their arrow keys
 *      (Radix menus, selects, comboboxes, sliders, tabs… — skipped via
 *      role check and `defaultPrevented`).
 *   2. Scrolls the nearest scrollable ancestor of the FOCUSED element,
 *      falling back to the element under the mouse cursor.
 *   3. When a dialog is open, scrolling is confined to containers
 *      inside that dialog (matches the focus trap).
 *
 * Pure frontend behavior — no data access, no PHI.
 */

const STEP = 60;

const KEY_MAP = {
  ArrowLeft: { horizontal: true, delta: -1 },
  ArrowRight: { horizontal: true, delta: 1 },
  ArrowUp: { horizontal: false, delta: -1 },
  ArrowDown: { horizontal: false, delta: 1 },
};

// Widgets that implement their own arrow-key behavior.
const OWNS_ARROWS =
  '[role="menu"],[role="menubar"],[role="listbox"],[role="combobox"],' +
  '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],' +
  '[role="option"],[role="radiogroup"],[role="slider"],[role="tablist"],' +
  '[role="tab"],[role="tree"],[role="grid"],[data-radix-select-viewport]';

const isEditable = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return true;
  return !!(el.isContentEditable || el.closest?.('[contenteditable="true"],[contenteditable=""]'));
};

const canScroll = (el, horizontal, delta) => {
  if (!el || el.nodeType !== 1) return false;
  let style;
  try { style = getComputedStyle(el); } catch { return false; }
  const overflow = horizontal ? style.overflowX : style.overflowY;
  if (!/(auto|scroll|overlay)/.test(overflow)) return false;
  if (horizontal) {
    if (el.scrollWidth <= el.clientWidth + 1) return false;
    return delta < 0
      ? el.scrollLeft > 0
      : el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }
  if (el.scrollHeight <= el.clientHeight + 1) return false;
  return delta < 0
    ? el.scrollTop > 0
    : el.scrollTop + el.clientHeight < el.scrollHeight - 1;
};

// Nearest scrollable ancestor (inclusive), stopping at <body>.
const findScrollableUp = (start, horizontal, delta, boundary) => {
  let el = start && start.nodeType === 1 ? start : null;
  while (el && el !== document.body && el !== document.documentElement) {
    if (canScroll(el, horizontal, delta)) return el;
    if (boundary && el === boundary) return null;
    el = el.parentElement;
  }
  return null;
};

// First scrollable descendant (used for open dialogs whose focus sits
// on the dialog root rather than the inner scroll container).
const findScrollableWithin = (root, horizontal, delta) => {
  if (!root) return null;
  if (canScroll(root, horizontal, delta)) return root;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let count = 0;
  let node = walker.nextNode();
  while (node && count < 400) {
    if (canScroll(node, horizontal, delta)) return node;
    node = walker.nextNode();
    count += 1;
  }
  return null;
};

const topOpenDialog = () => {
  const dialogs = document.querySelectorAll(
    '[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"]'
  );
  return dialogs.length ? dialogs[dialogs.length - 1] : null;
};

export const initKeyboardScrollNav = () => {
  let hoverEl = null;
  document.addEventListener('pointerover', (e) => { hoverEl = e.target; }, { passive: true });

  document.addEventListener('keydown', (e) => {
    const dir = KEY_MAP[e.key];
    if (!dir || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

    const active = document.activeElement;
    const target = e.target && e.target.nodeType === 1 ? e.target : null;
    if (isEditable(active) || isEditable(target)) return;
    if (target?.closest?.(OWNS_ARROWS) || (active?.closest && active.closest(OWNS_ARROWS))) return;

    const dialog = topOpenDialog();
    const inDialog = (el) => !!(el && dialog && dialog.contains(el));

    // Candidate starting points, most intentional first.
    const starts = [];
    if (active && active !== document.body) starts.push(active);
    if (hoverEl && hoverEl.isConnected) starts.push(hoverEl);

    let scroller = null;
    for (const start of starts) {
      if (dialog && !inDialog(start)) continue; // focus-trap parity
      scroller = findScrollableUp(start, dir.horizontal, dir.delta, dialog);
      if (scroller) break;
    }
    if (!scroller && dialog) {
      scroller = findScrollableWithin(dialog, dir.horizontal, dir.delta);
    }
    if (!scroller) return;

    e.preventDefault();
    if (dir.horizontal) scroller.scrollLeft += dir.delta * STEP;
    else scroller.scrollTop += dir.delta * STEP;
  });
};

export default initKeyboardScrollNav;
